import { useRef, useEffect } from 'react';
import { keyframes, css, jsx } from '@emotion/react';
import { labelStyles, withModifier } from '@zapier/style-encapsulation';
import { Animation } from '../../../theme/Animation.es.js';
import { Colors } from '../../../theme/Colors.es.js';
import { Shadows } from '../../../theme/Shadows.es.js';
import '../../../theme/Typography.es.js';
import { Zindexes } from '../../../theme/Zindexes.es.js';
import { useClickOutside } from '../../../hooks/useClickOutside.es.js';
import { useIntersect } from '../../../hooks/useIntersect.es.js';

const menuOpenAnimation = keyframes({
  "0%": {
    transform: "translateY(-10px)",
    opacity: 0
  },
  // This "delay" gives enough time for the menu to be measured
  // by `useIntersect` and flip upwards if it needs to, without
  // flickering for a moment while it renders down and is measured.
  "30%": {
    transform: "translateY(-10px)",
    opacity: 0
  },
  "100%": {
    transform: "translateY(0)",
    opacity: 1
  }
});
const DISTANCE = "calc(100% + 5px)";
const Styles = labelStyles("FloatingBox", {
  root: (props, derivedState) => [
    css({
      animation: `${Animation.transitionDuration} ease-out ${menuOpenAnimation}`,
      maxWidth: props.maxWidth,
      position: "absolute",
      minWidth: props.minWidth,
      zIndex: Zindexes.dropdownMenu
    }),
    props.align === "left" && withModifier(props.align, {
      left: 0
    }),
    props.align === "right" && withModifier(props.align, {
      right: 0
    }),
    props.align === "stretch" && withModifier(props.align, {
      left: 0,
      right: 0
    }),
    derivedState.position === "south" && withModifier(derivedState.position, {
      top: DISTANCE
    }),
    derivedState.position === "north" && withModifier(derivedState.position, {
      bottom: DISTANCE
    })
  ],
  box: (backgroundColor, borderRadius, borderColor) => css({
    backgroundColor,
    border: `1px solid ${borderColor}`,
    borderRadius: `var(--zds-radius-${borderRadius})`,
    boxShadow: Shadows.elevation20
  })
});
function getBackgroundColor(hasWhiteBackground = false) {
  return hasWhiteBackground ? "#fff" : Colors.BackgroundWeaker;
}
const FloatingBox = ({ children, hasWhiteBackground = void 0, intersectHeight = void 0, maxWidth = void 0, minWidth = void 0, onClickOutside = () => {
}, align = "left", position = void 0, borderRadius = "small", borderColor = Colors.StrokeWeaker }) => {
  const threshold = 0.9;
  const node = useRef(null);
  const [setIntersectNode, { isBottomIntersecting, isTopIntersecting }] = useIntersect({
    heightOverride: intersectHeight,
    once: true,
    threshold
  });
  const backgroundColor = getBackgroundColor(hasWhiteBackground);
  const [setClickOutsideNode] = useClickOutside({
    onClickOutside
  });
  useEffect(() => {
    setIntersectNode(node.current);
    setClickOutsideNode(node.current);
  }, [setClickOutsideNode, setIntersectNode]);
  const derivedState = {
    position: position || (isTopIntersecting || !isBottomIntersecting ? "south" : "north")
  };
  return jsx(
    "div",
    { css: Styles.root({ maxWidth, minWidth, align }, derivedState), ref: node, "data-testid": "floating-box", "data-zds": true },
    jsx("div", { css: Styles.box(backgroundColor, borderRadius, borderColor) }, children)
  );
};

export { FloatingBox };
//# sourceMappingURL=FloatingBox.es.js.map
